import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ImagePlus, Plus } from 'lucide-react';

type GalleryItem = {
  id: string;
  src: string;
  alt: string;
};

type GallerySlot = {
  id: string;
  src: string;
  alt: string;
};

function normalizeGalleryItems(content?: Record<string, unknown>): GalleryItem[] {
  const itemsRaw = Array.isArray(content?.items) ? content.items : [];
  return itemsRaw
    .map((item, index) => {
      const itemRecord =
        item && typeof item === 'object' ? (item as Record<string, unknown>) : null;
      if (!itemRecord) return null;
      const src =
        typeof itemRecord.src === 'string' ? itemRecord.src.trim() : '';
      if (!src) return null;
      return {
        id:
          typeof itemRecord.id === 'string' && itemRecord.id.trim().length > 0
            ? itemRecord.id.trim()
            : `gallery-item-${index + 1}`,
        src,
        alt:
          typeof itemRecord.alt === 'string' ? itemRecord.alt : `Gallery image ${index + 1}`,
      };
    })
    .filter((item): item is GalleryItem => Boolean(item));
}

function normalizeGallerySlots(content?: Record<string, unknown>): GallerySlot[] {
  const itemsRaw = Array.isArray(content?.items) ? content.items : [];
  return itemsRaw.map((item, index) => {
    const itemRecord =
      item && typeof item === 'object' ? (item as Record<string, unknown>) : null;
    return {
      id:
        typeof itemRecord?.id === 'string' && itemRecord.id.trim().length > 0
          ? itemRecord.id.trim()
          : `gallery-item-${index + 1}`,
      src: typeof itemRecord?.src === 'string' ? itemRecord.src.trim() : '',
      alt:
        typeof itemRecord?.alt === 'string' ? itemRecord.alt : `Gallery image ${index + 1}`,
    };
  });
}

interface GalleryBlockSurfaceProps {
  content?: Record<string, unknown>;
  style?: Record<string, unknown>;
  moveMode: boolean;
  isInteractPreview?: boolean;
  enableEditorSlots?: boolean;
  onOpenContent?: (galleryItemId?: string) => void;
  selectionOverlay: React.ReactNode;
}

export function GalleryBlockSurface({
  content,
  style,
  moveMode,
  isInteractPreview = false,
  enableEditorSlots = false,
  onOpenContent,
  selectionOverlay,
}: GalleryBlockSurfaceProps) {
  const items = React.useMemo(() => normalizeGalleryItems(content), [content]);
  const slots = React.useMemo(() => {
    const rawSlots = normalizeGallerySlots(content);
    const columnsCandidate = Number(content?.columns);
    const columns = Number.isFinite(columnsCandidate)
      ? Math.max(1, Math.min(4, Math.round(columnsCandidate)))
      : 3;
    const layoutVariant = String(content?.layoutVariant ?? '').toLowerCase();
    const baselineSlots =
      layoutVariant === 'hero-carousel' || layoutVariant === 'product-gallery'
        ? 4
        : layoutVariant === 'captioned-grid'
          ? 4
          : layoutVariant === 'polaroid-showcase' || layoutVariant === 'premium-showcase'
            ? 3
            : Math.max(columns * 2, 4);
    const nextSlots = rawSlots.length > 0 ? [...rawSlots] : [];
    while (nextSlots.length < baselineSlots) {
      nextSlots.push({
        id: `gallery-item-${nextSlots.length + 1}`,
        src: '',
        alt: `Gallery image ${nextSlots.length + 1}`,
      });
    }
    return nextSlots;
  }, [content]);
  const spacingCandidate = Number(content?.spacing);
  const spacing = Number.isFinite(spacingCandidate)
    ? Math.max(0, Math.min(32, Math.round(spacingCandidate)))
    : 8;
  const loop = content?.loop === true;
  const layoutVariant = String(content?.layoutVariant ?? '').toLowerCase();
  const showCaptions = content?.showCaptions === true;
  const columnsCandidate = Number(content?.columns);
  const columns = Number.isFinite(columnsCandidate)
    ? Math.max(1, Math.min(4, Math.round(columnsCandidate)))
    : 3;
  const imageFit =
    style?.imageFit === 'contain' || style?.imageFit === 'fill'
      ? style.imageFit
      : 'cover';
  const itemAspectRatio =
    style?.galleryItemAspectRatio === 'free' ||
    style?.galleryItemAspectRatio === '1:1' ||
    style?.galleryItemAspectRatio === '4:3' ||
    style?.galleryItemAspectRatio === '16:9' ||
    style?.galleryItemAspectRatio === '3:4'
      ? style.galleryItemAspectRatio
      : undefined;
  const imageFitClass =
    imageFit === 'contain'
      ? 'object-contain'
      : imageFit === 'fill'
        ? 'object-fill'
        : 'object-cover';
  const imageFrameClass = imageFit === 'contain' ? 'bg-stone-950/5' : '';
  const aspectClass =
    itemAspectRatio === 'free'
      ? 'min-h-0 h-full'
      : itemAspectRatio === '1:1'
        ? 'aspect-square'
        : itemAspectRatio === '16:9'
          ? 'aspect-[16/9]'
          : itemAspectRatio === '3:4'
            ? 'aspect-[3/4]'
            : 'aspect-[4/3]';
  const emblaOptions = React.useMemo(
    () => ({
      loop,
      align: 'start' as const,
      containScroll: 'trimSnaps' as const,
      watchDrag: !moveMode,
    }),
    [loop, moveMode]
  );
  const [emblaRef] = useEmblaCarousel(emblaOptions);

  const renderSlot = React.useCallback(
    (item: GallerySlot, className: string, showCaption = false, tiltClass = '') => (
      <div key={item.id} className={`relative overflow-hidden rounded-[18px] ${imageFrameClass} ${tiltClass} ${className}`}>
        {item.src ? (
          <img
            src={item.src}
            alt={item.alt}
            className={`h-full w-full ${imageFitClass}`}
            loading="lazy"
            draggable={false}
          />
        ) : isInteractPreview ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 border border-dashed border-stone-300 bg-white/70 text-stone-400">
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-300 bg-white text-stone-500 shadow-sm">
              <Plus className="h-5 w-5" />
            </span>
            <span className="text-xs font-medium">Empty image slot</span>
          </div>
        ) : (
          <button
            type="button"
            data-builder-allow-interaction
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onOpenContent?.(item.id);
            }}
            className="flex h-full w-full flex-col items-center justify-center gap-2 border border-dashed border-stone-300 bg-white/88 text-stone-500 transition hover:border-blue-400 hover:bg-blue-50/70"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm">
              <Plus className="h-5 w-5" />
            </span>
            <span className="text-xs font-medium">Add image</span>
          </button>
        )}
        {showCaption ? (
          <div className="absolute inset-x-2 bottom-2 rounded-full bg-white/92 px-2 py-1 text-[11px] text-stone-700 shadow-sm">
            {item.src ? item.alt : 'Caption'}
          </div>
        ) : null}
        {item.src && !moveMode && !isInteractPreview && onOpenContent ? (
          <button
            type="button"
            data-builder-allow-interaction
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onOpenContent(item.id);
            }}
            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-white/92 px-2 py-1 text-[11px] font-medium text-stone-700 shadow-sm hover:bg-white"
          >
            <ImagePlus className="h-3.5 w-3.5" />
            Replace
          </button>
        ) : null}
      </div>
    ),
    [imageFitClass, imageFrameClass, isInteractPreview, moveMode, onOpenContent]
  );

  const renderEditorLayout = React.useMemo(() => {
    if (layoutVariant === 'product-gallery' || layoutVariant === 'hero-carousel') {
      const heroSlot = slots[0];
      const thumbSlots = slots.slice(1, 4);
      return (
        <div className="grid h-full grid-rows-[1fr_auto] gap-2" style={{ gap: `${Math.max(6, spacing)}px` }}>
          {renderSlot(heroSlot, 'h-full w-full')}
          <div className="grid grid-cols-3 gap-2" style={{ gap: `${Math.max(6, spacing)}px` }}>
            {thumbSlots.map((slot) => renderSlot(slot, `${aspectClass} w-full`))}
          </div>
        </div>
      );
    }

    if (layoutVariant === 'editorial-strip' || layoutVariant === 'premium-showcase') {
      const leadSlot = slots[0];
      const sideSlots = slots.slice(1, 3);
      return (
        <div className="grid h-full grid-cols-[1.2fr_0.8fr] gap-2" style={{ gap: `${Math.max(6, spacing)}px` }}>
          {renderSlot(leadSlot, 'h-full w-full')}
          <div className="grid grid-rows-2 gap-2" style={{ gap: `${Math.max(6, spacing)}px` }}>
            {sideSlots.map((slot) => renderSlot(slot, 'h-full w-full'))}
          </div>
        </div>
      );
    }

    if (layoutVariant === 'polaroid-showcase') {
      return (
        <div className="flex h-full items-center justify-center gap-3">
          {slots.slice(0, 3).map((slot, index) =>
            renderSlot(
              slot,
              'flex h-[80%] w-[28%] flex-col rounded-[18px] border border-stone-300 bg-white p-2 shadow-md',
              false,
              index === 0 ? 'rotate-[-6deg]' : index === 1 ? 'rotate-[3deg]' : 'rotate-[-2deg]'
            )
          )}
        </div>
      );
    }

    const gridColumns = layoutVariant === 'captioned-grid' ? 2 : columns;
    return (
      <div
        className="grid h-full gap-2"
        style={{
          gap: `${spacing}px`,
          gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
        }}
      >
        {slots.map((slot) => renderSlot(slot, `${aspectClass} w-full`, showCaptions))}
      </div>
    );
  }, [columns, layoutVariant, renderSlot, showCaptions, slots, spacing]);

  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg border border-stone-200 bg-stone-100">
      {enableEditorSlots ? (
        <div
          className="h-full p-2"
          style={{ padding: `${Math.max(8, Math.min(16, spacing))}px` }}
        >
          {renderEditorLayout}
        </div>
      ) : items.length > 0 ? (
        <div
          ref={emblaRef}
          className={`h-full overflow-hidden ${moveMode ? 'pointer-events-none' : ''}`}
        >
          <div className="flex h-full">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="relative h-full min-w-0 flex-[0_0_100%]"
                style={{ paddingRight: index < items.length - 1 ? `${spacing}px` : undefined }}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className={`h-full w-full rounded-lg ${imageFitClass} ${imageFrameClass}`}
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex h-full w-full items-center justify-center text-xs text-stone-500">
          Gallery block
        </div>
      )}
      {selectionOverlay}
    </div>
  );
}
